import { filterCatalogs, filterCatalogsByReg, filterItems, filterSellers, sortItems } from './sorting';
import { getCatalogCardDatas, getItemCardDatas, getLiveItemCardDatas, getSellerCardDatas } from './cardData';
import { getItemIdsForCatalog, getItemIdsForSeller } from './item';
import { createSelector } from 'reselect';
import { getAllCurrentUserCatalogRegistrationIds } from './catalogRegistration';
import { getCatalogIdsForSeller } from './catalog';
import { getFollowedSellerIds } from './followSeller';
import { getLiveCatalogData } from '../../redux/modules/console';
import { getMyBidItemIds } from './biddingInfo';
import { getSavedSearchItemIds } from './savedSearchItem';
import { getSortedRecentItemIdsFromState } from './recentItem';
import { getSortedRecommendedItemIdsFromState } from './recommendedItem';
import { getSortedSavedItemIdsFromState } from './saveItem';
import { getStorefrontItemIds } from './storefrontPage';
import { itemSortOrders } from '../../enums';
import { type SellerCatalogWithIds } from '../../types/SellerCatalogWithIds';

export const getCatalogItems = (state, catalogId, { buyNow, count, live, page, sort, terms } = {}) => {
    const ids = getItemIdsForCatalog(state, catalogId);
    const all = getItemCardDatas(state, ids);
    const filterBuyNow = buyNow
        ? all.filter(x => x.buyNow.status === 'available')
        : Boolean(live)
        ? all.filter(
              x =>
                  Boolean(x.biddingInfo.isAvailable) &&
                  !x.biddingInfo.isPassed &&
                  !x.biddingInfo.isSold &&
                  ((x.biddingInfo.isLocked && x.liveItemStatus.open) ||
                      (!x.biddingInfo.isLocked && !x.liveItemStatus.open)),
          )
        : all;
    return filterItems(filterBuyNow, { count, page, sort, terms });
};

export const getNextCatalogItems = (state, catalogId, itemId, count) => {
    const { items } = getCatalogItems(state, catalogId, { sort: 'LOT_NUMBER_LOWEST_FIRST' }) || [];
    const [theItem] = items.filter(({ item }) => item.itemId === itemId);

    let returnItems = items.filter(({ item }) => {
        return item.index > theItem?.item?.index;
    });

    if (returnItems.length < count) {
        return [...returnItems, ...items.slice(0, count - returnItems.length)];
    } else {
        return returnItems.slice(0, count);
    }
};

export const getCatalogContainsBuyNowLots = (state, catalogId) => {
    const ids = getItemIdsForCatalog(state, catalogId);
    const all = getItemCardDatas(state, ids);
    const buyNowItems = all.filter(x => x.buyNow.status === 'available').length > 0;
    return buyNowItems;
};

export const getLiveCatalogItems = (state, catalogId, { count, page, sort, terms } = {}) => {
    const ids = getItemIdsForCatalog(state, catalogId);
    const all = getLiveItemCardDatas(state, ids);
    return filterItems(all, { count, page, sort, terms });
};

export const getFollowedSellers = (state, { count, page, sort, terms } = {}) => {
    const ids = getFollowedSellerIds(state);
    const all = getSellerCardDatas(state, ids);
    return filterSellers(all, { count, page, sort, terms });
};

export const getMyBids = (state, { count, filter, page, sort, terms, tab } = {}) => {
    const { all, past, upcoming } = getMyBidItemIds(state);
    const ids = tab === 'upcoming' ? upcoming : tab === 'ended' ? past : all;
    const cardData = getItemCardDatas(state, ids);
    let filteredItems;
    if (sort === 'TIME_SOONEST_FIRST') {
        filteredItems = stackSortedLiveSavedItems(state, cardData, { count, filter, page, sort, terms });
    } else {
        filteredItems = filterItems(cardData, { count, filter, page, sort, terms });
    }
    return filteredItems;
};

export const getMyRecentItems = (state, { count, page, sort, terms } = {}) => {
    const ids = getSortedRecentItemIdsFromState(state);
    const all = getItemCardDatas(state, ids);
    return filterItems(all, { count, page, sort, terms });
};

export const getMyRecommendedItems = (state, { count, page, sort, terms } = {}) => {
    const ids = getSortedRecommendedItemIdsFromState(state);
    const removableIds = getSortedRecentItemIdsFromState(state);
    const filteredIds = ids.filter(id => !removableIds.includes(id));
    const all = getItemCardDatas(state, filteredIds);
    return filterItems(all, { count, page, sort, terms });
};

export const getMySavedItems = (state, { count, page, sort, terms } = {}) => {
    const ids = getSortedSavedItemIdsFromState(state);
    const all = getItemCardDatas(state, ids);
    let filteredItems;
    if (sort === 'TIME_SOONEST_FIRST') {
        filteredItems = stackSortedLiveSavedItems(state, all, { count, page, sort, terms });
    } else {
        filteredItems = filterItems(all, { count, page, sort, terms });
    }
    return filteredItems;
};

const stackSortedLiveSavedItems = (state, all, { count, filter, page, sort, tab, terms }) => {
    const liveItems = [];
    const savedItems = [];
    const pastItems = [];
    all.forEach(item => {
        const { currentItemIndex } = getLiveCatalogData(state, item.catalog.catalogId);
        const lotsAway = item.item.index - currentItemIndex;
        if (currentItemIndex && lotsAway > -1) {
            item.lotsAway = lotsAway;
            liveItems.push(item);
        } else if (!item.biddingInfo.isAvailable) {
            pastItems.push(item);
        } else {
            savedItems.push(item);
        }
    });
    const sortedLiveItems = filterItems(liveItems, { sort: 'LOTS_AWAY_FIRST' });
    const sortedPastItems = filterItems(pastItems, { sort: 'TIME_ENDING_FURTHEST' });
    const sortedSavedItems = filterItems(savedItems, { sort });
    return filterItems([...sortedLiveItems.items, ...sortedSavedItems.items, ...sortedPastItems.items], {
        count,
        filter,
        page,
        tab,
        terms,
    });
};

export const getMySavedSearchItems = (state, { count, page, sort, terms } = {}) => {
    const ids = getSavedSearchItemIds(state);
    const all = getItemCardDatas(state, ids);
    return filterItems(all, { count, page, sort, terms });
};

export const getSellerCatalogs = (state, sellerId, { count, page, sort, tab, terms } = {}) => {
    const ids = getCatalogIdsForSeller(state, sellerId);
    const all = getCatalogCardDatas(state, ids);
    return filterCatalogs(all, { count, page, sort, tab, terms });
};

export const getStorefrontItems = (state, { buyNow, count, page, sort, terms } = {}) => {
    const ids = getStorefrontItemIds(state);
    const all = getItemCardDatas(state, ids);
    const filterBuyNow = buyNow ? all.filter(x => x.buyNow.status === 'available') : all;
    return filterItems(filterBuyNow, { count, page, sort, terms });
};

export const getMyAuctions = (state, { count, page, sort, terms, tab } = {}) => {
    const catalogIds = getAllCurrentUserCatalogRegistrationIds(state);
    const all = getCatalogCardDatas(state, catalogIds);
    return filterCatalogsByReg(state, all, { count, page, sort, tab, terms });
};

export const getMostPopularItemsByBids = (state, sellerId) => {
    const ids = getItemIdsForSeller(state, sellerId);
    const all = getItemCardDatas(state, ids);
    const { items } = filterItems(all, { count: 20, page: 1, sort: 'MOST_BIDS', tab: 'upcoming' });
    return items || [];
};

export const getTrendingItemsByBids = (state, trendingItemIds, count) => {
    const trendingItems = getItemCardDatas(state, trendingItemIds);
    const { items } = filterItems(trendingItems, {
        count,
        page: 1,
        sort: 'MOST_BIDS',
        tab: 'upcoming',
    });
    return items || [];
};

export const comingUpItemsSelector = createSelector(getMySavedItems, getMyBids, (savedItems, myBidItems) => {
    const ids = [];
    let comingUpItems = [];
    //This logic is needed to filter out duplicates. An item could be both saved and bid on, and we only want to display it once.
    savedItems.items.forEach(item => {
        if (item.biddingInfo.isAvailable) {
            ids.push(item.item.itemId);
            comingUpItems.push(item);
        }
    });
    myBidItems.items.forEach(item => {
        if (item.biddingInfo.isAvailable && !ids.includes(item.item.itemId)) {
            comingUpItems.push(item);
        }
    });
    comingUpItems = sortItems(comingUpItems, itemSortOrders.DATE_ASCENDING);
    return comingUpItems;
});

export const getCatalogsForFollowedSellers: SellerCatalogWithIds = state => {
    const followedSellerIds = getFollowedSellerIds(state);
    let followedSellerCatalogs = [];
    let followedSellerCatalogIds = [];
    // limiting the number of items per seller to keep data small
    followedSellerIds.forEach(id => {
        const liveCatalogs = getSellerCatalogs(state, id, {
            count: 20,
            page: 1,
            tab: 'live',
        });
        if (liveCatalogs && liveCatalogs.items && liveCatalogs.items.length > 0) {
            followedSellerCatalogs = followedSellerCatalogs.concat(liveCatalogs.items);
            liveCatalogs.items.forEach(item => {
                if (item && item.catalog && item.catalog.catalogId) {
                    followedSellerCatalogIds = followedSellerCatalogIds.concat(item.catalog.catalogId);
                }
            });
        }
    });
    return { catalogIds: followedSellerCatalogIds, catalogs: followedSellerCatalogs };
};
